import { makeStyles } from '@material-ui/core'
import React from 'react'
import { DayPickerSingleDateController } from 'react-dates'
import classnames from 'classnames'
import type { Moment } from 'moment'
import moment from 'moment'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import IconButton from '../IconButton'
import DatePickerMonthDropdown from './DatePickerMonthDropdown'
import { useDatePickerStyles } from './useDatePickerStyles'
import type { CalendarEvent } from 'paintscout'
import { getCrews } from '@paintscout/util/builder'
import { useClientOptions } from '../../providers'

const useStyles = makeStyles((theme) => ({
  dayWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  day: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '35px !important',
    height: '35px !important',
    borderRadius: theme.borderRadius.md,
    [theme.breakpoints.up('sm')]: {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        color: theme.palette.text.primary
      }
    }
  },
  selectedDay: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.common.white} !important`
  },
  today: {
    border: `1px solid ${theme.palette.primary.main}`
  },
  eventIndicators: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    width: '44px',
    marginLeft: '1px',
    bottom: '-9px'
  },
  eventIndicator: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginLeft: '1px',
    marginRight: '1px'
  },
  eventOverflow: {
    position: 'absolute',
    marginLeft: '1px',
    bottom: '-20px',
    color: theme.palette.text.primary
  }
}))

export interface SingleDatePickerControllerProps {
  date: Moment | null
  onChange: (date: Moment | null) => void
  recalculateTableHeight?: () => void
  getEventsForDay?: (day: Moment) => CalendarEvent[]
  blockPastDays?: boolean
}

export default function SingleDatePickerController(props: SingleDatePickerControllerProps) {
  const { date, getEventsForDay, recalculateTableHeight, onChange, blockPastDays } = props
  const classes = useStyles()
  const datePickerClasses = useDatePickerStyles({ isSingle: true, blockPastDays })
  const { options } = useClientOptions()
  const crewOptions = getCrews({ options })

  const getEventColor = (event: CalendarEvent) => {
    if (!event.crew) {
      return event.estimator.color
    }

    const eventCrew = crewOptions.find((crew) => crew._id === event.crew)
    return eventCrew?.color || event.estimator.color
  }

  const recalculateTableHeightWithDelay = () => {
    if (recalculateTableHeight) {
      setTimeout(() => recalculateTableHeight(), 1) // Slight delay to allow the month to change
    }
  }

  return (
    <DayPickerSingleDateController
      transitionDuration={0}
      onFocusChange={(_) => {}}
      date={date}
      onDateChange={onChange}
      daySize={45}
      noBorder
      focused
      initialVisibleMonth={() => date || moment()}
      renderDayContents={(day, _) => {
        const events = getEventsForDay?.(day) || []
        return (
          <span className={classes.dayWrapper}>
            <span
              className={classnames({
                [classes.day]: true,
                [classes.selectedDay]: date && date.isSame(day, 'day'),
                [classes.today]: day.isSame(moment(), 'day')
              })}
            >
              {day.format('D')}
            </span>
            {events.length ? (
              <span className={classes.eventIndicators}>
                {events.slice(0, 3).map((event, index) => (
                  <span
                    key={`${day.day()}-event-${index}`}
                    className={classes.eventIndicator}
                    style={{ backgroundColor: getEventColor(event) }}
                  />
                ))}
              </span>
            ) : null}
            {events.length > 3 && <span className={classes.eventOverflow}>...</span>}
          </span>
        )
      }}
      navPrev={
        <IconButton className={datePickerClasses.navButton} size="small">
          <ChevronLeftIcon />
        </IconButton>
      }
      navNext={
        <IconButton className={classnames(datePickerClasses.navButton, datePickerClasses.rightNavButton)} size="small">
          <ChevronRightIcon />
        </IconButton>
      }
      onPrevMonthClick={recalculateTableHeight}
      onNextMonthClick={recalculateTableHeight}
      renderMonthElement={(args) => (
        <DatePickerMonthDropdown
          {...args}
          onYearSelect={(currentMonth, newMonth) => {
            args.onYearSelect(currentMonth, newMonth)
            recalculateTableHeightWithDelay()
          }}
          onMonthSelect={(currentMonth, newMonth) => {
            args.onMonthSelect(currentMonth, newMonth)
            recalculateTableHeightWithDelay()
          }}
        />
      )}
      enableOutsideDays
      isDayBlocked={(day) => {
        return blockPastDays ? day.isBefore(moment(), 'day') : false
      }}
    />
  )
}
